import { useState } from 'react';
import {
  fill, JOB_MESSAGE_KIND_HINTS, JOB_MESSAGE_KIND_LABELS, Size, t,
  type JobMessageContact, type JobMessageKind, type JobMessageKindRow, type JobMessageRuleView, type JobMessageRulesAnswer,
} from '@bms2/shared';
import { Button, Checkbox } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import { del, put, useApi } from './api';
import styles from './JobMessageRules.module.css';

/** 🔒 The customer's own message rules (decision 2080), read and set on the Field Service tile. */
export const customerMessagesPath = (companyId: string): string => `/work/messages/customers/${encodeURIComponent(companyId)}`;
/** 🔒 One Deal's message rules beside the customer's they override or follow (decision 2080), under the Deal's Work Orders. */
export const dealMessagesPath = (dealId: string, kind?: JobMessageKind): string => `/work/messages/deals/${encodeURIComponent(dealId)}${kind ? `/${kind}` : ''}`;

interface Editing {
  kind: JobMessageKind;
  isEnabled: boolean;
  employmentIds: string[];
  /** The version of the rule at this level that the editor opened on; null where it sets one that is not there (T1363). */
  version: number | null;
}

function namesOf(rule: JobMessageRuleView, contacts: readonly JobMessageContact[]): string {
  return rule.employmentIds.map((id) => contacts.find((contact) => contact.employmentId === id)?.name).filter(Boolean).join(', ');
}

function ruleLine(rule: JobMessageRuleView | null, contacts: readonly JobMessageContact[]): string {
  if (!rule) return t('jobMessages.notSet');
  if (!rule.isEnabled) return t('jobMessages.off');
  const names = namesOf(rule, contacts);
  return names ? `${t('jobMessages.on')} · ${fill(t('jobMessages.to'), { names })}` : t('jobMessages.on');
}

/**
 * 🔒 The customer message rules (piece 29; decisions 1915, 2080; T1264). One
 * row per kind, Booked, On My Way, Reminder and Complete, each reading On with
 * whom it goes to, Off, or not set. On a Deal a kind with no rule of its own
 * reads the customer's, and Use the Customer's hands a set kind back. 🔨 A
 * recipient is ticked from the customer's contacts, never typed.
 */
export function JobMessageRules({ companyId, dealId = null, apiBaseUrl = '' }: { companyId?: string | null; dealId?: string | null; apiBaseUrl?: string }) {
  const path = dealId ? dealMessagesPath(dealId) : companyId ? customerMessagesPath(companyId) : null;
  const fetched = useApi<JobMessageRulesAnswer>(path, apiBaseUrl);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // A viewer the route refuses reads no rules at all, rather than a failed panel.
  if (!path || fetched.denied) return null;

  const open = (row: JobMessageKindRow) => {
    const from = row.own ?? row.customer;
    setProblem(null);
    setSaved(false);
    setEditing({ kind: row.kind, isEnabled: from?.isEnabled ?? true, employmentIds: from?.employmentIds ?? [], version: row.own?.version ?? null });
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    const outcome = await put<JobMessageRulesAnswer>(path, editing, apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setProblem(outcome.message);
      return;
    }
    setEditing(null);
    setSaved(true);
    fetched.reload();
  };

  const handBack = async (kind: JobMessageKind) => {
    if (!dealId) return;
    setBusy(true);
    const outcome = await del<JobMessageRulesAnswer>(dealMessagesPath(dealId, kind), apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setProblem(outcome.message);
      return;
    }
    setEditing(null);
    fetched.reload();
  };

  return (
    <div className={styles.panel} data-job-messages={dealId ? 'deal' : 'customer'}>
      <AsyncStatePanel state={fetched.state} emptyAction={t('jobMessages.customerHint')} onRetry={fetched.reload}>
        {(answer) => (
          <>
            {dealId ? <h3 className={styles.title}>{t('jobMessages.title')}</h3> : null}
            <p className={styles.hint}>{t(dealId ? 'jobMessages.dealHint' : 'jobMessages.customerHint')}</p>
            {saved ? <p className={styles.good} role="status">{t('jobMessages.saved')}</p> : null}
            <ul className={styles.rows}>
              {answer.rows.map((row) => {
                const follows = dealId !== null && row.own === null;
                return (
                  <li key={row.kind} className={styles.row} data-job-message={row.kind} data-job-message-state={(row.own ?? row.customer) === null ? 'unset' : (row.own ?? row.customer)!.isEnabled ? 'on' : 'off'}>
                    <div className={styles.what}>
                      <span className={styles.kind}>{JOB_MESSAGE_KIND_LABELS[row.kind]}</span>
                      <span className={styles.hint}>{JOB_MESSAGE_KIND_HINTS[row.kind]}</span>
                    </div>
                    <div className={styles.state}>
                      {dealId ? <span className={styles.level}>{t(follows ? 'jobMessages.followsCustomer' : 'jobMessages.forThisDeal')}</span> : null}
                      <span>{ruleLine(follows ? row.customer : row.own, answer.contacts)}</span>
                    </div>
                    {answer.canEdit ? (
                      <div className={styles.actions}>
                        <Button size={Size.Small} text={[t('jobMessages.edit')]} actions={[() => open(row)]} />
                        {dealId && row.own ? <Button size={Size.Small} text={[t('jobMessages.useCustomers')]} disabled={busy} actions={[() => void handBack(row.kind)]} /> : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {editing ? (
              <Modal
                open
                title={fill(t('jobMessages.editTitle'), { kind: JOB_MESSAGE_KIND_LABELS[editing.kind] })}
                subtitle={t(dealId ? 'jobMessages.forThisDeal' : 'jobMessages.title')}
                onCancel={() => setEditing(null)}
                dataModal="job-message-rule"
                actions={[
                  { kind: 'primary', label: t('jobMessages.save'), disabled: busy, onAct: () => void save() },
                  { kind: 'cancel', label: t('modal.cancel'), onAct: () => setEditing(null) },
                ]}
              >
                <p className={styles.hint}>{JOB_MESSAGE_KIND_HINTS[editing.kind]}</p>
                {problem ? <p className={styles.problem} role="alert">{problem}</p> : null}
                <Checkbox label={t('jobMessages.sendIt')} checked={editing.isEnabled} onChange={(on) => setEditing({ ...editing, isEnabled: on })} name="job-message-enabled" />
                <h3 className={styles.title}>{t('jobMessages.recipients')}</h3>
                <p className={styles.hint}>{t('jobMessages.recipientsHint')}</p>
                {answer.contacts.length === 0 ? <p className={styles.hint}>{t('jobMessages.noContacts')}</p> : answer.contacts.map((contact) => (
                  <span key={contact.employmentId} data-job-message-recipient={contact.employmentId}>
                    <Checkbox
                      label={[contact.name, contact.title, [contact.email, contact.mobile].filter(Boolean).join(' · ') || t('jobMessages.noWay')].filter(Boolean).join(' · ')}
                      checked={editing.employmentIds.includes(contact.employmentId)}
                      onChange={(on) => setEditing({ ...editing, employmentIds: on ? [...editing.employmentIds, contact.employmentId] : editing.employmentIds.filter((id) => id !== contact.employmentId) })}
                      name={`job-message-recipient-${contact.employmentId}`}
                    />
                  </span>
                ))}
              </Modal>
            ) : null}
          </>
        )}
      </AsyncStatePanel>
    </div>
  );
}
